import React from 'react';
import { Dog, CheckCircle, AlertCircle, CreditCard, Clock } from 'lucide-react';
import LoadingSpinner from '../common/LoadingSpinner';
import { Pet } from '../../types';
import { calculateAge } from '../../utils/ageCalculation';

interface PetListProps {
  myPets: Pet[];
  registeredPetsInSession: any[];
  onAddPetClick: () => void;
  onViewPetDetails: (pet: Pet) => void;
  onPayRegistrationFee: (pet: Pet) => void;
  onResumeDraft?: (pet: Pet) => void;
  onDeleteDraft?: (pet: Pet) => void;
  deletingDraftId?: number | null;
  isLoading: boolean;
  localPetImages?: Map<number, string>;  // Map of pet_id (integer) -> blob URL
}

const PetList: React.FC<PetListProps> = ({ myPets, registeredPetsInSession, onAddPetClick, onViewPetDetails, onPayRegistrationFee, onResumeDraft, onDeleteDraft, deletingDraftId = null, isLoading, localPetImages }) => {
  // Debug: log localPetImages when it changes
  React.useEffect(() => {
    if (localPetImages && localPetImages.size > 0) {
      if (import.meta.env.DEV) {
        console.log('🖼️ PetList received localPetImages:', Array.from(localPetImages.entries()));
      }
    }
  }, [localPetImages]);

  // Whole-card tap is a mobile-only affordance: on desktop the cursor + in-card
  // buttons already make actions clear, so we only wire up card clicks below the
  // `sm` breakpoint (<640px) to avoid hijacking desktop clicks.
  const [isMobile, setIsMobile] = React.useState<boolean>(
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 639px)').matches : false
  );
  React.useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    setIsMobile(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const getPaymentUi = (pet: Pet) => {
    const statusRaw = (pet as any).stripe_payment_status;
    const status = String(statusRaw || 'pending').toLowerCase();

    if (status === 'paid' || status === 'exempted') {
      return {
        badgeClass: 'bg-green-50 text-green-700 border-green-200',
        label: status === 'exempted' ? 'Exempted' : 'Paid',
        Icon: CheckCircle,
        showPay: false,
        actionLabel: '',
      };
    }

    if (status === 'failed') {
      return {
        badgeClass: 'bg-red-50 text-red-700 border-red-200',
        label: 'Failed',
        Icon: AlertCircle,
        showPay: true,
        actionLabel: 'Retry payment',
      };
    }

    return {
      badgeClass: 'bg-amber-50 text-amber-800 border-amber-200',
      label: 'Due',
      Icon: CreditCard,
      showPay: true,
      actionLabel: 'Pay now',
    };
  };
  
  return (
    <div className="lg:col-span-2 bg-white border border-gray-200 rounded-lg shadow-sm">
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4">
              <Dog className="w-full h-full text-gray-600" />
            </div>
            <span className="text-base font-medium text-gray-900">My Animals</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onAddPetClick}
              className="pmc-btn-primary px-4 py-2.5 rounded-full text-xs font-medium min-h-[44px] touch-target hover:opacity-95 transition-opacity"
            >
              Add Pet
            </button>
          </div>
        </div>
      </div>
      <div className="p-4">
        <div className="space-y-3 mt-4 max-h-96 lg:max-h-none overflow-y-auto lg:overflow-visible scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="medium" text="Loading pets..." />
            </div>
          ) : myPets.length > 0 ? (
            myPets.map(pet => {
              const Icon = (pet as any).icon;
              const isNewlyRegistered = registeredPetsInSession.some(regPet => regPet?.pets?.[0]?.pet_name === pet.name);
              const isInactive = pet.status?.toLowerCase() === 'inactive';
              const isDraft = pet.status?.toLowerCase() === 'drafted';
              const isPendingPayment = pet.status === 'PendingPayment';
              const paymentStatus = String((pet as any).stripe_payment_status || '').toLowerCase();
              const petRole = ((pet as any).pet_role || (pet as any).animalType || '').toLowerCase();
              const isPaymentPending = isPendingPayment;
              const isPaymentFailed = isPendingPayment && paymentStatus === 'failed';
              const isTrueDraft = isDraft;
              const isFeeExempt = petRole === 'esa' || petRole === 'sa' || paymentStatus === 'exempted';
              const paymentUi = getPaymentUi(pet);

              // Prioritize local image URL over backend URL for immediate display
              const localImageUrl = pet?.id ? localPetImages?.get(pet.id) : undefined;
              const imageUrl = localImageUrl || pet.image_url;
              
              if (import.meta.env.DEV) {
                console.log(`🔍 Pet image (${pet.name}, id=${pet.id}):`, {
                  local: Boolean(localImageUrl),
                  backend: Boolean(pet.image_url),
                });
              }
              
              // Whole-card click routes to the most relevant action for the pet's
              // state: resume a draft, jump to payment when pending/failed, else
              // open details. Inner buttons call stopPropagation so they win.
              const handleCardClick = () => {
                if (isTrueDraft) {
                  if (deletingDraftId !== pet.id) onResumeDraft?.(pet);
                } else if (isPaymentPending) {
                  onPayRegistrationFee(pet);
                } else {
                  onViewPetDetails(pet);
                }
              };

              return (
                <div key={pet.id}
                  role={isMobile ? 'button' : undefined}
                  tabIndex={isMobile ? 0 : undefined}
                  onClick={isMobile ? handleCardClick : undefined}
                  onKeyDown={isMobile ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleCardClick();
                    }
                  } : undefined}
                  className={`border rounded-lg p-3 transition-all ${isMobile ? 'cursor-pointer hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-300' : ''} ${
                    isPaymentFailed
                      ? 'bg-red-50 border-red-300 border-dashed'
                      : isPaymentPending
                        ? 'bg-amber-50 border-amber-300 border-dashed'
                        : isTrueDraft
                        ? 'bg-blue-50 border-blue-200 border-dashed'
                        : isInactive 
                          ? 'bg-gray-100 border-gray-300 opacity-75'
                          : isNewlyRegistered
                            ? 'bg-green-50 border-green-300 hover:border-green-400'
                            : 'bg-gray-50 border-gray-200'
                  }`}>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-3">
                      {imageUrl ? (
                        <img 
                          src={imageUrl} 
                          alt={pet.name} 
                          className="w-10 h-10 rounded-full object-cover border-2 border-gray-300 shadow-sm" 
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gray-100 border-2 border-gray-300 flex items-center justify-center">
                          <Icon className={`w-5 h-5 ${isInactive ? 'text-gray-400' : 'text-gray-600'}`} />
                        </div>
                      )}
                      <div>
                        <p className={`text-sm font-medium ${isPaymentFailed ? 'text-red-900' : isPaymentPending ? 'text-amber-900' : isTrueDraft ? 'text-blue-800' : isInactive ? 'text-gray-600' : 'text-gray-900'}`}>
                          {isTrueDraft && !pet.name?.trim() ? 'Untitled Draft' : pet.name}
                          {isPaymentFailed && (
                            <span className="ml-2 text-xs text-red-700 font-normal">
                              (Payment Failed)
                            </span>
                          )}
                          {isPaymentPending && !isPaymentFailed && (
                            <span className="ml-2 text-xs text-amber-700 font-normal">
                              (Awaiting Payment)
                            </span>
                          )}
                          {isTrueDraft && (
                            <span className="ml-2 text-xs text-blue-600 font-normal">
                              (Draft)
                            </span>
                          )}
                          {isNewlyRegistered && !isInactive && !isDraft && (
                            <span className="ml-2 text-xs text-green-600 font-normal">
                              ✨ Just added!
                            </span>
                          )}
                          {isInactive && (
                            <span className="ml-2 text-xs text-gray-500 font-normal">
                              (Inactive)
                            </span>
                          )}
                        </p>
                        <p className={`text-xs ${isInactive ? 'text-gray-500' : 'text-gray-600'}`}>
                          {pet.breed ? `${pet.breed} | ` : ''}{(() => {
                            // Calculate age from date_of_birth if available, otherwise use stored age
                            const calculatedAge = (pet as any).date_of_birth 
                              ? calculateAge((pet as any).date_of_birth)
                              : (typeof pet.age === 'object' ? pet.age.years || 0 : pet.age);
                            return calculatedAge === 0 ? 'Less than a year' : `${calculatedAge} ${calculatedAge === 1 ? 'year' : 'years'} old`;
                          })()}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 ml-[52px] sm:ml-0 sm:justify-end">
                      {isPaymentPending ? (
                        <>
                          {!isPaymentFailed && (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-full border text-[11px] font-medium bg-amber-50 text-amber-800 border-amber-200"
                              title="Registration complete — awaiting payment"
                            >
                              <Clock className="w-3.5 h-3.5" />
                              <span>Awaiting Payment</span>
                            </span>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); onPayRegistrationFee(pet); }}
                            className={`flex items-center gap-1 px-3 py-1.5 text-xs text-white rounded-full transition-colors cursor-pointer font-medium uppercase ${
                              isPaymentFailed
                                ? 'bg-red-600 hover:bg-red-700'
                                : 'bg-[color:var(--pmc-primary)] hover:bg-[color:var(--pmc-primary)]'
                            }`}
                          >
                            <CreditCard className="w-3.5 h-3.5" />
                            {isPaymentFailed ? 'Retry payment' : 'Pay now'}
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); onViewPetDetails(pet); }}
                            className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded transition-colors cursor-pointer font-medium uppercase"
                          >
                            View/Edit
                          </button>
                        </>
                      ) : isTrueDraft ? (
                        <div className="flex flex-wrap items-center gap-2">
                          {deletingDraftId === pet.id ? (
                            <span className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 font-medium">
                              <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                              </svg>
                              Deleting...
                            </span>
                          ) : (
                            <>
                              <button
                                onClick={(e) => { e.stopPropagation(); onDeleteDraft?.(pet); }}
                                className="flex items-center gap-1 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-full transition-colors cursor-pointer font-medium uppercase border border-red-200"
                              >
                                Delete
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); onResumeDraft?.(pet); }}
                                className="flex items-center gap-1 px-3 py-1.5 text-xs text-white bg-[color:var(--pmc-primary)] hover:bg-[color:var(--pmc-primary)] rounded-full transition-colors cursor-pointer font-medium uppercase"
                              >
                                Resume
                              </button>
                            </>
                          )}
                        </div>
                      ) : (
                        <>
                          {!isInactive && !isFeeExempt && (
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-[11px] font-medium ${paymentUi.badgeClass}`}
                              title={`Registration fee payment: ${paymentUi.label}`}
                            >
                              <paymentUi.Icon className="w-3.5 h-3.5" />
                              <span>Payment: {paymentUi.label}</span>
                            </span>
                          )}
                          {!isInactive && !isFeeExempt && paymentUi.showPay && (
                            <button
                              onClick={(e) => { e.stopPropagation(); onPayRegistrationFee(pet); }}
                              className="flex items-center gap-1 px-2 py-1 text-xs text-[color:var(--pmc-primary)] hover:bg-indigo-50 rounded transition-colors cursor-pointer font-medium uppercase"
                            >
                              <CreditCard className="w-3.5 h-3.5" />
                              {paymentUi.actionLabel}
                            </button>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); onViewPetDetails(pet); }}
                            className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded transition-colors cursor-pointer font-medium uppercase"
                          >
                            {isInactive ? 'View' : 'View/Edit'}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="mt-2 flex flex-col gap-1 ml-[52px] sm:ml-6 text-xs text-gray-600">
                    <p className={`text-xs ${isInactive ? 'text-gray-500' : 'text-gray-600'}`}>
                      Registered {new Date((pet as any).registeredDate || pet.registered_date || pet.created_at || new Date()).toLocaleDateString('en-US', { timeZone: 'UTC' })}
                    </p>
                    {isInactive && (pet as any).move_out_date && (
                      <p className="text-xs text-gray-500">
                        Moved Out: {new Date((pet as any).move_out_date).toLocaleDateString('en-US', { timeZone: 'UTC' })}
                      </p>
                    )}
                    {isInactive && (pet as any).deactivated_reason && (pet as any).deactivated_reason !== 'Moved Out' && (
                      <p className="text-xs text-gray-500">
                        Reason: {(pet as any).deactivated_reason}
                      </p>
                    )}
                    {isPaymentFailed && (
                      <span className="text-[11px] italic font-normal text-red-600">Payment failed — please retry to complete registration</span>
                    )}
                    {isPaymentPending && !isPaymentFailed && (
                      <span className="text-[11px] italic font-normal text-amber-700">Registration complete — payment required</span>
                    )}
                    {isTrueDraft && (
                      <span className="text-[11px] italic font-normal text-blue-500">Draft — click Resume to continue registration</span>
                    )}
                    {!isInactive && !isDraft && !isPendingPayment && (
                      <span>Compliance: <span className={`font-medium ${
                        pet.status === 'Active' ? 'text-green-600' : 
                        pet.status === 'Pending' ? 'text-orange-600' : 
                        pet.status === 'Rejected' ? 'text-red-600' : 
                        pet.status === 'Expired' ? 'text-gray-600' : 'text-gray-600'
                      }`}>{pet.status}</span></span>
                    )}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Dog className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No pets registered yet</p>
              <button
                onClick={onAddPetClick}
                className="mt-3 pmc-text-primary hover:underline"
              >
                Register your first pet
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PetList;


